import {Component, Inject, OnInit} from '@angular/core';
import {Observable} from "rxjs";
import {CentreOption} from "../../../../../interfaces/Centres";
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {Employee} from "../../../../../interfaces/Employee";
import {InductionService} from "../../../../education/components/induction/induction.service";
import {ActivatedRoute, Router} from "@angular/router";
import {CentreService} from "../../../../../services/centre.service";
import {EmployeeService} from "../../../../../services/employee.service";
import {LogService} from "../../../../../services/log.service";
import firebase from "firebase/compat/app";
import {MatSnackBar} from "@angular/material/snack-bar";
import {GroupSessionTopics} from "../group-session-topics.model";
import {GroupSessionTopicsService} from "../group-session-topics.service";
import {AngularFireStorage} from "@angular/fire/compat/storage";
import {LoadingService} from "../../../../../services/loading.service";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";

@Component({
  selector: 'app-add-edit-group-session-topics',
  templateUrl: './add-edit-group-session-topics.component.html',
  styleUrls: ['./add-edit-group-session-topics.component.scss']
})
export class AddEditGroupSessionTopicsComponent implements OnInit {
  allCenters$!: Observable<CentreOption[]>;
  displayCentre = (c: any) => c?.centreName;
  sessionDisplay = (value: string) => value;
  subjects: string[] = ['Language', 'Math', 'EVS & GK'];
  subjectsDisplay = (value: string) => value;
  groupSessionForm !: FormGroup;
  observerName: any
  allTeachers: any[]=[]
  employeeData!: Employee[];
  currentEditId:any;
  isLoading: boolean = false;
  selectedCentre:any;
  selectedDetailedFile: any; // For single detailedDescription file
  selectedPresentationFiles: any; // For multiple presentationUrls files
  data: any; // To store existing record data
  MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB size limit
  ALLOWED_FILE_TYPES = [
    'application/pdf',
    'application/msword', // .doc
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
    'application/vnd.ms-powerpoint', // .ppt
    'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
    'text/plain' // .txt
  ];

  constructor(private fb: FormBuilder,
              private groupSessionService : GroupSessionTopicsService,
              private router: Router,
              private route : ActivatedRoute,
              private centreService : CentreService,
              private userService: EmployeeService,
              private snackBar : MatSnackBar,
              private mLogService : LogService,
              private storage : AngularFireStorage,
              private loader : LoadingService,
              @Inject(MAT_DIALOG_DATA) public passedData: any,
              private dialogRef: MatDialogRef<AddEditGroupSessionTopicsComponent>
              ) {
  }

  ngOnInit(): void {
    this.formInit();

    if (this.passedData) {
      // This means EDIT mode
      this.currentEditId = this.passedData.id;
      this.data = { ...this.passedData };

      this.groupSessionForm.patchValue({
        sr: this.data.sr || 0,
        content: this.data.content,
        description: this.data.description,
        detailedDescription: this.data.detailedDescription || '',
        presentationUrls: this.data.presentationUrls || []
      });
    }
  }


  formInit() {
    this.groupSessionForm = this.fb.group({
      sr: [0, [Validators.required, Validators.min(1)]],
      content: ['', Validators.required],
      detailedDescription: ['',],
      presentationUrls: [[]],
      description: [''],
    });
  }

  async submitForm() {
    if (this.groupSessionForm.invalid) return;

    this.isLoading = true;
    this.loader?.setLoading(true);

    // 🔹 Get Logged-in User
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    const username = `${userData.userName || ''}`.trim() || 'Unknown User';

    const payload: GroupSessionTopics = {
      sr: this.groupSessionForm.value.sr,
      content: this.groupSessionForm.value.content,
      description: this.groupSessionForm.value.description,
      detailedDescription: this.data?.detailedDescription || '', // Keep existing single file
      presentationUrls: this.data?.presentationUrls ? [...this.data.presentationUrls] : [] // Keep existing array
    };

    try {
      // ------------ FILE UPLOAD FOR DETAILED DESCRIPTION (SINGLE FILE) ------------
      if (this.selectedDetailedFile) {
        const file = this.selectedDetailedFile;

        if (this.currentEditId) {
          // Editing → store inside existing ID folder
          const filePath = `groupSessionFiles/${this.currentEditId}/detailed/${file.name}`;
          const fileRef = this.storage.ref(filePath);
          await fileRef.put(file);
          const downloadURL = await fileRef.getDownloadURL().toPromise();
          payload.detailedDescription = downloadURL; // Replace with new file
        } else {
          // Creating → store using timestamp
          const timestamp = Date.now();
          const filePath = `groupSessionFiles/${timestamp}/detailed/${file.name}`;
          const fileRef = this.storage.ref(filePath);
          await fileRef.put(file);
          const downloadURL = await fileRef.getDownloadURL().toPromise();
          payload.detailedDescription = downloadURL;
        }
      }

      // ------------ FILE UPLOAD FOR PRESENTATION URLS (MULTIPLE FILES) ------------
      if (this.selectedPresentationFiles?.length) {
        const presentationUrls: string[] = [];

        if (this.currentEditId) {
          // Editing → store inside existing ID folder
          for (let file of this.selectedPresentationFiles) {
            const filePath = `groupSessionFiles/${this.currentEditId}/presentation/${file.name}`;
            const fileRef = this.storage.ref(filePath);
            await fileRef.put(file);
            const downloadURL = await fileRef.getDownloadURL().toPromise();
            presentationUrls.push(downloadURL);
          }
        } else {
          // Creating → store using timestamp
          const timestamp = Date.now();
          for (let file of this.selectedPresentationFiles) {
            const filePath = `groupSessionFiles/${timestamp}/presentation/${file.name}`;
            const fileRef = this.storage.ref(filePath);
            await fileRef.put(file);
            const downloadURL = await fileRef.getDownloadURL().toPromise();
            presentationUrls.push(downloadURL);
          }
        }

        // Add new files to existing array
        payload.presentationUrls = payload.presentationUrls
          ? [...payload.presentationUrls, ...presentationUrls]
          : presentationUrls;
      }

      // ------------ UPDATE OR CREATE ------------
      if (this.currentEditId) {
        // ---------------- UPDATE ----------------
        payload.updatedBy = username;
        payload.updatedAt = firebase.firestore.FieldValue.serverTimestamp();

        await this.groupSessionService.update(this.currentEditId, payload);

        this.mLogService.addLog({
          date: Date.now(),
          section: 'Group Session Topics',
          action: 'Update',
          description: `${username} updated the Group Session Topics record`,
          currentIp: localStorage.getItem('currentip')!
        });

        this.afterSave('Updated successfully!');

      } else {
        // ---------------- CREATE ----------------
        payload.createdBy = username;
        payload.createdAt = firebase.firestore.FieldValue.serverTimestamp();
        payload.status = 'Active';

        await this.groupSessionService.create(payload);

        this.mLogService.addLog({
          date: Date.now(),
          section: 'Group Session Topics',
          action: 'Create',
          description: `${username} created a new Group Session Topics record`,
          currentIp: localStorage.getItem('currentip')!
        });

        this.afterSave('Saved successfully!');
      }

    } catch (error) {
      console.error('Error saving Group Session Topics:', error);
      this.isLoading = false;
      this.loader?.setLoading(false);
      this.showSnackbar('Failed to save Group Session Topics.', 'Close', 'error');
    }
  }

  private afterSave(message: string) {
    this.isLoading = false;
    this.loader?.setLoading(false);
    this.showSnackbar(message, 'Close', 'success');
    this.dialogRef.close(this.currentEditId ? 'updated' : 'saved');
    this.groupSessionForm.reset();
    this.currentEditId = null;
    this.selectedDetailedFile = null;
    this.selectedPresentationFiles = null;
  }

  resetForm() {
    this.groupSessionForm.reset();
    this.currentEditId = null;
    this.selectedDetailedFile = null;
    this.selectedPresentationFiles = null;
  }

  onCancel() {
    this.dialogRef.close();
  }

  get suggestionsLength(): number {
    const val: string = this.groupSessionForm.get('description')?.value || '';
    return val.length;
  }

  onDetailedDescriptionFileChange(event: any): void {
    const files = event.target.files;

    if (!files || files.length === 0) {
      return;
    }

    const file = files[0]; // Only take the first file

    // Validate file
    let isValid = true;
    let errorMessage = '';

    // Check file size
    if (file.size > this.MAX_FILE_SIZE) {
      isValid = false;
      errorMessage = `File "${file.name}" exceeds 25MB size limit.`;
    }

    // Check file type
    if (isValid && !this.ALLOWED_FILE_TYPES.includes(file.type)) {
      isValid = false;
      errorMessage = `File "${file.name}" has an invalid type. Only documents are allowed (no ZIP files).`;
    }

    if (!isValid) {
      this.groupSessionForm.get('detailedDescription')?.setErrors({
        invalidFile: true,
        message: errorMessage
      });
      this.showSnackbar(errorMessage, 'Close', 'error');
      this.selectedDetailedFile = null;
      event.target.value = '';
    } else {
      this.groupSessionForm.get('detailedDescription')?.setErrors(null);
      this.selectedDetailedFile = file;
    }
  }

  onPresentationFileChange(event: any): void {
    this.handleFileChange(event, 'presentationUrls', (files) => {
      this.selectedPresentationFiles = files;
    });
  }

  private handleFileChange(event: any, fieldName: string, setFiles: (files: any) => void): void {
    const files = event.target.files;

    if (!files || files.length === 0) {
      return;
    }

    // Validate each file
    let isValid = true;
    let errorMessage = '';

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      // Check file size
      if (file.size > this.MAX_FILE_SIZE) {
        isValid = false;
        errorMessage = `File "${file.name}" exceeds 25MB size limit.`;
        break;
      }

      // Check file type
      if (!this.ALLOWED_FILE_TYPES.includes(file.type)) {
        isValid = false;
        errorMessage = `File "${file.name}" has an invalid type. Only documents are allowed (no ZIP files).`;
        break;
      }
    }

    if (!isValid) {
      // Set validation error on the form control
      this.groupSessionForm.get(fieldName)?.setErrors({
        invalidFile: true,
        message: errorMessage
      });
      this.showSnackbar(errorMessage, 'Close', 'error');
      setFiles(null);
      event.target.value = ''; // Clear the file input
    } else {
      // Clear any previous errors
      this.groupSessionForm.get(fieldName)?.setErrors(null);
      setFiles(files);
    }
  }

  removeDetailedFile(): void {
    if (this.data?.detailedDescription) {
      this.data.detailedDescription = '';
      this.groupSessionForm.patchValue({
        detailedDescription: ''
      });
    }
  }

  removePresentationFile(index: number): void {
    if (this.data?.presentationUrls) {
      this.data.presentationUrls.splice(index, 1);
      this.groupSessionForm.patchValue({
        presentationUrls: this.data.presentationUrls
      });
    }
  }

  showSnackbar(message: string, action: string, type: 'success' | 'error' = 'success') {
    this.snackBar.open(message, action, {
      duration: 3000,
      panelClass: type === 'success' ? 'snackbar-success' : 'snackbar-error'
    });
  }

  extractFileName(url: string): string {
    const decoded = decodeURIComponent(url); // remove %2F etc.
    const fileWithQuery = decoded.split('/').pop() || "";
    return fileWithQuery.split('?')[0]; // remove ?token
  }
}
